import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from 'contexts/AuthContext';
import LoginEmail from './components/LoginEmail';
import EmailValidation from 'pages/EmailValidation';
import DashboardPage from './pages/DashboardPage';
import DashboardFinanceiro from './features/financeiro/pages/DashboardFinanceiro';
import DashboardContratos from './features/contratos/pages/DashboardContratos';
import CentralIA from './features/ia/pages/CentralIA';
import GerenciarInscricoes from './features/integracoes/pages/GerenciarInscricoes';
import TesteN8n from './features/integracoes/pages/TesteN8n';
import DashboardComercial from './features/comercial/pages/DashboardComercial';
import Operacional from './features/operacional/pages/Operacional';
import Estoque from './features/estoque/pages/Estoque';
import Administrativo from './features/administrativo/pages/Administrativo';
import Automacoes from './features/automatizacoes/pages/Automacoes';
import DepartamentoPessoal from './features/dp/pages/DepartamentoPessoal';
import Infraestrutura from './features/infraestrutura/pages/Infraestrutura';
import Cobranca from './features/cobranca/pages/Cobranca';
import GestaoSolicitacoes from './features/administrativo/components/GestaoSolicitacoes';
import ConfiguracoesPage from './features/configuracoes/pages/ConfiguracoesPage';
import UserProfile from './components/UserProfile';
import ErrorBoundary from './components/common/ErrorBoundary';
import EnhancedProtectedPage from './components/common/EnhancedProtectedPage';
import './index.css';
import LoadingSpinner from './components/common/LoadingSpinner';
import { hasCompanyContext as checkHasCompanyContext } from './utils/setCompanyContext';
import { getMainDashboardPermissions } from './utils/dashboardPermissions';
import NotFound from 'pages/NotFound';

// Componente para verificar contexto de empresa
const CompanyContextProvider = ({ children }) => {
  const [checkingContext, setCheckingContext] = React.useState(true);

  React.useEffect(() => {
    const checkCompanyContext = () => {
      try {
        checkHasCompanyContext();
      } catch (error) {
        console.warn('Erro ao verificar contexto de empresa:', error);
      } finally {
        setCheckingContext(false);
      }
    };

    checkCompanyContext();
  }, []);

  if (checkingContext) {
    return <LoadingSpinner />;
  }

  // Sempre permitir acesso, mas mostrar seletor se necessário
  return children;
};

// Componente para proteger rotas autenticadas
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return user ? (
    <CompanyContextProvider>
      {children}
    </CompanyContextProvider>
  ) : <Navigate to="/login" replace />;
};

// Componente para redirecionar usuários já logados
// Componente principal da aplicação
const AppContent = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          user ? (
            <Navigate to="/dashboard" />
          ) : (
            <ErrorBoundary>
              <LoginEmail />
            </ErrorBoundary>
          )
        } 
      />
      <Route 
        path="/validate-email/:token" 
        element={
          <ErrorBoundary>
            <EmailValidation />
          </ErrorBoundary>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <EnhancedProtectedPage requiredPermissions={getMainDashboardPermissions()} pageName="Dashboard Principal" useAllPermissions={true}>
              <DashboardPage />
            </EnhancedProtectedPage>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/financeiro" 
        element={
          <ProtectedRoute>
            <EnhancedProtectedPage requiredPermissions={['financeiro.view']} pageName="Dashboard Financeiro">
              <DashboardFinanceiro />
            </EnhancedProtectedPage>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/contratos" 
        element={
          <ProtectedRoute>
            <EnhancedProtectedPage requiredPermissions={['contratos.view']} pageName="Dashboard Contratos">
              <DashboardContratos />
            </EnhancedProtectedPage>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/agente-ia" 
        element={
          <ProtectedRoute>
            <CentralIA />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/gerenciar-inscricoes" 
        element={
          <ProtectedRoute>
            <GerenciarInscricoes />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/teste-n8n" 
        element={<TesteN8n />} 
      />
      <Route
        path="/comercial"
        element={
          <ProtectedRoute>
            <EnhancedProtectedPage requiredPermissions={['comercial.view', 'comercial.ranking.view', 'comercial.minhas_vendas.view']} pageName="Dashboard Comercial">
              <DashboardComercial />
            </EnhancedProtectedPage>
          </ProtectedRoute>
        }
      />
      <Route 
        path="/operacional" 
        element={
          <ProtectedRoute>
            <EnhancedProtectedPage requiredPermissions={['operacional.view', 'operacional.agendamentos.view', 'operacional.edit']} pageName="Dashboard Operacional">
              <Operacional />
            </EnhancedProtectedPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/estoque"
        element={
          <ProtectedRoute>
            <EnhancedProtectedPage requiredPermissions={['estoque.view']} pageName="Dashboard Estoque">
              <Estoque />
            </EnhancedProtectedPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/departamento-pessoal"
        element={
          <ProtectedRoute>
            <EnhancedProtectedPage requiredPermissions={['dp.view']} pageName="Departamento Pessoal">
              <DepartamentoPessoal />
            </EnhancedProtectedPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/infraestrutura"
        element={
          <ProtectedRoute>
            <EnhancedProtectedPage requiredPermissions={['infra.view']} pageName="Diretoria de Infraestrutura">
              <Infraestrutura />
            </EnhancedProtectedPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cobranca"
        element={
          <ProtectedRoute>
            <EnhancedProtectedPage requiredPermissions={['cobranca.view']} pageName="Diretoria de Cobrança">
              <Cobranca />
            </EnhancedProtectedPage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/administrativo" 
        element={
          <ProtectedRoute>
            <EnhancedProtectedPage requiredPermissions={['administrativo.view']} pageName="Dashboard Administrativo">
              <Administrativo />
            </EnhancedProtectedPage>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/automacoes" 
        element={
          <ProtectedRoute>
            <EnhancedProtectedPage requiredPermissions={['automacoes.view']} pageName="Dashboard Automações">
              <Automacoes />
            </EnhancedProtectedPage>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/gestao-solicitacoes" 
        element={
          <ProtectedRoute>
            <EnhancedProtectedPage requiredPermissions={['solicitacoes.view']} pageName="Gestão de Solicitações">
              <GestaoSolicitacoes />
            </EnhancedProtectedPage>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/configuracoes" 
        element={
          <ProtectedRoute>
            <EnhancedProtectedPage requiredPermissions={['config.usuarios.view', 'config.preset.view', 'config.bank_credentials.view']} pageName="Configurações">
              <ConfiguracoesPage />
            </EnhancedProtectedPage>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/perfil" 
        element={
          <ProtectedRoute>
            <UserProfile />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/" 
        element={<Navigate to={user ? "/dashboard" : "/login"} />} 
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App() {
  // Configurar serviço n8n na inicialização
  React.useEffect(() => {
    // Conexão assumida como OK - removido teste que causava delay de 30s
    
    
    // Listener para dados do n8n
    const handleN8nData = (_event) => {
      
      // Aqui você pode atualizar o estado global ou notificar componentes
    };
    
    window.addEventListener('n8nDataUpdate', handleN8nData);
    
    return () => {
      window.removeEventListener('n8nDataUpdate', handleN8nData);
    };
  }, []);

  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppContent />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App; 






